import { useEffect, useState } from 'react';

import { Flex, FloraButton, Toaster, Tooltip } from '@grupoboticario/flora-react';
import {
  InformationCircleIcon,
  BarcodeFillIcon,
  ArrowRightIcon,
  PixOutlineIcon,
} from '@grupoboticario/flora-react-icons';
import { useSearchParams, Link } from 'react-router-dom';

import { ResponsiveFloraTypography, EudoraLogo, Tag } from 'components';
import config from 'config';
import { PAYMENT_STATUS, QUERYPARAMS } from 'config/environments/shared';
import { BusinessUnit } from 'domains/brand.d';
import { FeatureFlag } from 'domains/feature-flag.d';
import { PaymentType } from 'domains/payment-titles.d';
import { copyText, getFormattedPrice, getShortDate, getPaymentStatus } from 'helpers';
import { useFeatureFlag, usePaymentDetail, useTracking } from 'hooks';
import { newRelicMonitoringService } from 'services';
import { useGlobalContext } from 'store';

import Styles from './Card.styles';

import type { PaymentTitleType } from 'domains/payment-titles.d';

export const CardPayment = ({
  payment,
  hasLogo,
}: {
  payment: PaymentTitleType;
  hasLogo?: boolean;
}) => {
  const {
    user: { isMultiBrand },
  } = useGlobalContext();
  const [brand, setBrand] = useState(config.app.slugname);
  const [searchParams, setSearchParams] = useSearchParams();
  const { sendDataToGTM } = useTracking();
  const {
    data: paymentDetailData,
    isError: isErrorPaymentDetail,
    isLoading: isLoadingPaymentDetail,
    isSuccess: isSuccessPaymentDetail,
    trigger: triggerPaymentDetail,
  } = usePaymentDetail(searchParams.get(QUERYPARAMS.PAYMENT_ID) || '');
  const paymentStatus = getPaymentStatus({
    daysLate: payment.daysLate,
    daysUntilDueDate: payment.daysUntilDueDate,
    paymentStatus: payment.paymentStatus,
    status: payment.status,
  });
  const isSetDrawer: boolean =
    searchParams.get(QUERYPARAMS.PAYMENT_ID) === payment.paymentTitleId &&
    !!searchParams.get(QUERYPARAMS.IS_DRAWER);
  const isSetClipboard: boolean =
    searchParams.get(QUERYPARAMS.PAYMENT_ID) === payment.paymentTitleId &&
    !!searchParams.get('isClipboard');
  const isBoleto = payment.paymentType === PaymentType.Boleto;
  const isExpired = payment.status === PAYMENT_STATUS.EXPIRED;
  const { value: isVDFPaymentEnable, loading: isLoadingVDFFeatureFlag } = useFeatureFlag(
    FeatureFlag.ENABLE_VDF_PAYMENT,
    false
  );

  const openPaymentDetail = () => {
    setSearchParams({ drawer: 'PaymentDetail', isOpen: 'true', paymentId: payment.paymentTitleId });
  };

  const handleDrawer = () => {
    setSearchParams({ paymentId: payment.paymentTitleId, isDrawer: 'true' });

    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'interaction_a_pagar',
        cd_interaction_detail: `${brand}:click:ver-detalhes`,
        transaction_id: payment.orderId,
        cd_metodo_pagamento: payment.planDetail.toLowerCase(),
        cd_status_pagamento: getPaymentStatus({
          daysLate: payment.daysLate,
          daysUntilDueDate: payment.daysUntilDueDate,
          paymentStatus: payment.paymentStatus,
          status: payment.status,
        })?.tag.toLowerCase(),
        cd_dias_para_vencimento: payment.daysUntilDueDate,
        cd_dias_apos_vencimento: payment.daysLate,
      },
    });
    return triggerPaymentDetail();
  };

  const handleCopy = () => {
    setSearchParams({ paymentId: payment.paymentTitleId, isClipboard: 'true' });

    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'interaction_a_pagar',
        cd_interaction_detail: isBoleto
          ? `${brand}:click:copiar-codigo`
          : `${brand}:click:pix-copia-e-cola`,
        transaction_id: payment.orderId,
        cd_metodo_pagamento: payment.planDetail.toLowerCase(),
        cd_status_pagamento: getPaymentStatus({
          daysLate: payment.daysLate,
          daysUntilDueDate: payment.daysUntilDueDate,
          paymentStatus: payment.paymentStatus,
          status: payment.status,
        })?.tag.toLowerCase(),
        cd_dias_para_vencimento: payment.daysUntilDueDate,
        cd_dias_apos_vencimento: payment.daysLate,
      },
    });
    return triggerPaymentDetail();
  };

  const handleOnClick = () => {
    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'interaction_a_pagar',
        cd_interaction_detail: `${brand}:click:tooltip`,
        transaction_id: payment.orderId,
        cd_metodo_pagamento: payment.planDetail.toLowerCase(),
        cd_status_pagamento: getPaymentStatus({
          daysLate: payment.daysLate,
          daysUntilDueDate: payment.daysUntilDueDate,
          paymentStatus: payment.paymentStatus,
          status: payment.status,
        })?.tag.toLowerCase(),
        cd_dias_para_vencimento: payment.daysUntilDueDate,
        cd_dias_apos_vencimento: payment.daysLate,
      },
    });
  };

  useEffect(() => {
    if (isSetDrawer) {
      if (isSuccessPaymentDetail) {
        openPaymentDetail();
      }

      if (isErrorPaymentDetail) {
        Toaster.notify({
          kind: 'error',
          description: `Não foi possível exibir os detalhes do pagamento. Por favor, tente novamente.`,
          origin: 'right-top',
        });
      }
    }

    if (isSetClipboard) {
      const message = isBoleto ? 'do boleto' : 'Pix';

      if (isSuccessPaymentDetail) {
        copyText(paymentDetailData.barCode)
          .then(() => {
            Toaster.notify({
              kind: 'success',
              description: `Código ${message} copiado com sucesso!`,
              origin: 'right-top',
            });
          })
          .catch((e: Error) => {
            newRelicMonitoringService.addPageAction('callback_copiar_codigo', {
              cd_interaction_detail: `erro:${e?.message}`,
            });
          })
          .finally(() => {
            setSearchParams({});
          });
      }

      if (isErrorPaymentDetail) {
        Toaster.notify({
          kind: 'error',
          description: `Não foi possível copiar o código ${message}. Por favor, tente novamente.`,
          origin: 'right-top',
        });
        newRelicMonitoringService.addPageAction('callback_copiar_codigo', {
          cd_interaction_detail: 'erro',
          cd_numero_pedido: `${payment.orderId}`,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isErrorPaymentDetail, isLoadingPaymentDetail, isSuccessPaymentDetail, searchParams]);

  useEffect(() => {
    if (isVDFPaymentEnable && !isLoadingVDFFeatureFlag) {
      setBrand(payment.brand?.toLocaleLowerCase() === BusinessUnit.EUD ? 'eudora' : 'boticario');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingVDFFeatureFlag]);

  return (
    <Styles.Card>
      <Styles.CardHeader align='flex-start' justify={'space-between'} data-has-logo={hasLogo}>
        <Flex direction='column' align='flex-start'>
          <Styles.Title
            data-has-bot={payment.brand?.toLocaleLowerCase() !== BusinessUnit.EUD}
            direction='column'
            justify='center'
            align='flex-start'
          >
            <ResponsiveFloraTypography
              fontSize='subtitle'
              fontWeight='bold'
              as='h2'
              color='$backgroundAltPrimary'
              aria-label={`Pagamento no valor de ${getFormattedPrice(payment.amount)}`}
            >
              {getFormattedPrice(payment.amount)}
            </ResponsiveFloraTypography>
          </Styles.Title>
          <Styles.LogoImage data-logo>
            {isVDFPaymentEnable && isMultiBrand && (
              <>
                {payment.brand?.toLocaleLowerCase() === BusinessUnit.EUD && (
                  <EudoraLogo variant='default' viewBox='5 0 250 30' />
                )}
              </>
            )}
          </Styles.LogoImage>
        </Flex>

        <Flex direction='column' align='end' gap='$2'>
          <Flex align='center' data-testid='tooltip'>
            {paymentStatus && paymentStatus.toolTip && (
              <Tooltip
                align='start'
                direction='bottom'
                showArrow
                size='standard'
                text={paymentStatus.toolTip}
              >
                <Styles.ButtonTooltip
                  hierarchy='tertiary'
                  has='iconOnly'
                  onClick={handleOnClick}
                  aria-label=''
                  data-button-tooltip
                  icon={<InformationCircleIcon color='$nonInteractivePredominant' size={24} />}
                />
              </Tooltip>
            )}
            {paymentStatus && paymentStatus.tagVariant && (
              <Tag
                data={{
                  ...paymentStatus,
                  tagText: paymentStatus.tag,
                  subTitleText: paymentStatus.subTitle || '',
                }}
                type='pagamento'
              />
            )}
          </Flex>
        </Flex>
      </Styles.CardHeader>

      <Styles.Divider aria-hidden />

      <Styles.Value align='center' justify='space-between'>
        <ResponsiveFloraTypography
          fontSize='bodyLargeStandard'
          fontWeight='regular'
          color='$nonInteractiveAuxiliar'
        >
          {paymentStatus?.subTitle}
        </ResponsiveFloraTypography>
        <ResponsiveFloraTypography
          fontWeight='medium'
          fontSize='bodyLargeShort'
          color='$nonInteractivePredominant'
        >
          {getShortDate(payment.dueDate)}
        </ResponsiveFloraTypography>
      </Styles.Value>

      <Styles.Value align='center' justify='space-between'>
        <ResponsiveFloraTypography
          fontWeight='regular'
          color='$nonInteractiveAuxiliar'
          fontSize='bodyLargeStandard'
        >
          Forma de pagamento
        </ResponsiveFloraTypography>
        <ResponsiveFloraTypography
          fontWeight='medium'
          fontSize='bodyLargeShort'
          color='$nonInteractivePredominant'
        >
          {isBoleto ? 'Boleto' : 'Parcelado com Pix'}
        </ResponsiveFloraTypography>
      </Styles.Value>

      <Styles.Value align='center' justify='space-between'>
        <ResponsiveFloraTypography
          fontSize='bodyLargeStandard'
          fontWeight='regular'
          color='$nonInteractiveAuxiliar'
        >
          Pedido
        </ResponsiveFloraTypography>
        <ResponsiveFloraTypography
          fontWeight='medium'
          fontSize='bodyLargeShort'
          color='$nonInteractivePredominant'
        >
          {payment.orderId}
        </ResponsiveFloraTypography>
      </Styles.Value>

      <Styles.Divider aria-hidden />

      <Flex justify='space-between' gap='$3' css={{ width: '100%' }}>
        {!isExpired && (
          <FloraButton
            aria-label={isBoleto ? `Copiar código do Boleto` : 'Copiar código do Pix'}
            data-id={payment.orderId}
            disabled={isLoadingPaymentDetail && isSetClipboard}
            onClick={handleCopy}
            isFull
            has='iconRight'
            icon={isBoleto ? <BarcodeFillIcon /> : <PixOutlineIcon />}
            role='button'
            hierarchy='secondary'
          >
            <ResponsiveFloraTypography fontSize='bodySmallShort' fontWeight='bold'>
              {isLoadingPaymentDetail && isSetClipboard
                ? 'Copiando...'
                : isBoleto
                ? 'Copiar código'
                : 'Pix copia e cola'}
            </ResponsiveFloraTypography>
          </FloraButton>
        )}

        {!isExpired && (
          <FloraButton
            aria-label='Acessar detalhes do pagamento'
            data-id={payment.orderId}
            onClick={handleDrawer}
            isFull
            has='iconRight'
            icon={<ArrowRightIcon />}
            role='button'
            isLoading={isLoadingPaymentDetail && isSetDrawer}
            disabled={isLoadingPaymentDetail && isSetDrawer}
          >
            <ResponsiveFloraTypography fontWeight='bold' fontSize='bodySmallShort'>
              {isLoadingPaymentDetail && isSetDrawer ? 'Carregando...' : 'Ver detalhes'}
            </ResponsiveFloraTypography>
          </FloraButton>
        )}

        {isExpired && (
          <FloraButton
            aria-label={`Acessar renegociação do pedido ${payment.orderId}`}
            as={Link}
            data-id={payment.orderId}
            isFull
            has='iconRight'
            icon={<ArrowRightIcon />}
            role='button'
            to={`/pedidos?drawer=Renegotiation&isOpen=true&paymentId=${payment.paymentTitleId}`}
          >
            <ResponsiveFloraTypography fontWeight='bold' fontSize='bodySmallShort'>
              {isBoleto ? 'Renegociar boleto' : 'Renegociar pagamento'}
            </ResponsiveFloraTypography>
          </FloraButton>
        )}
      </Flex>
    </Styles.Card>
  );
};
